import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Box
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MailIcon from "@mui/icons-material/Mail";
import FolderIcon from "@mui/icons-material/Folder";
import PreviewIcon from "@mui/icons-material/Preview";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PolicyIcon from '@mui/icons-material/Policy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import IDPIcon from "./IDPIcon";
import DocumentPipelineIcon from "./DocumentPipelineIcon";
import checkAccess from "../../modules/security/CheckAccess";

import logo from "../../assets/IDPIcon.webp"; // Import your logo here

const drawerWidthOpen = 240;
const drawerWidthClosed = 70;

const sidebarItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/", permission: { object: "ui-dashboard", action: "view" } },
  { text: "HealthXtract", icon: <CloudUploadIcon />, path: "/process-docs", permission: { object: "ui-healthxtract", action: "view" } },
  { text: "Document Center", icon: <FolderIcon />, path: "/doc-center", permission: { object: "ui-doc-center", action: "view" } },
  {
    text: "Pipeline Center",
    icon: <DocumentPipelineIcon />,
    path: "/pipeline-center",
    permission: { object: "ui-pipeline-center", action: "view" },
    subItems: [
      { text: "Create Pipeline", icon: <AddCircleOutlineIcon />, path: "/pipeline-center/create", permission: { object: "ui-pipeline", action: "create" } },
      { text: "Pipeline List", icon: <ListAltIcon />, path: "/pipeline-center/list", permission: { object: "ui-pipeline", action: "list" } }
    ]
  },
  {
    text: "Admin Panel",
    icon: <AdminPanelSettingsIcon />,
    path: "/admin-center",
    permission: { object: "ui-admin-center", action: "view" },
    subItems: [
      { text: "Entitlements", icon: <PolicyIcon />, path: "/admin-center/entitlements", permission: { object: "ui-admin-center-entitlements", action: "manage" } }
    ]
  },
  { text: "Manual Review", icon: <PreviewIcon />, path: "/manual-review", permission: { object: "ui-manual-review", action: "access" } },
  { text: "Fax", icon: <MailIcon />, path: "/fax", permission: { object: "ui-fax", action: "read" } },
];

function Sidebar() {
  const [open, setOpen] = useState(true);
  const [openMenus, setOpenMenus] = useState({});
  const [filteredItems, setFilteredItems] = useState([]);

  useEffect(() => {
    const filterItems = async () => {
      const allowed = [];
      for (const item of sidebarItems) {
        if (!item.permission || await checkAccess(item.permission.object, item.permission.action)) {
          const newItem = { ...item };
          if (item.subItems) {
            newItem.subItems = [];
            for (const sub of item.subItems) {
              if (!sub.permission || await checkAccess(sub.permission.object, sub.permission.action)) {
                newItem.subItems.push(sub);
              }
            }
          }
          allowed.push(newItem);
        }
      }
      setFilteredItems(allowed);
    };
    filterItems();
  }, []);

  const handleToggle = (text) => {
    setOpenMenus(prev => ({ ...prev, [text]: !prev[text] }));
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidthOpen : drawerWidthClosed,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidthOpen : drawerWidthClosed,
          background: "#0d1b2a",
          color: "#00f5d4",
          boxShadow: "4px 0px 10px rgba(0, 234, 255, 0.3)",
          overflowX: "hidden",
          transition: "width 0.3s ease"
        },
      }}
    >
      <Box display="flex" alignItems="center" p={1} sx={{ justifyContent: open ? "space-between" : "center" }}>
        {open && (
          <Box component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: 32,
              ml: 1,
              transition: "opacity 0.3s ease",
            }}
          />
        )}
        <IconButton onClick={() => setOpen(!open)} sx={{ color: "#00f5d4" }}>
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <List sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {filteredItems.map(item => (
          <React.Fragment key={item.text}>
            <ListItem
              button
              onClick={() => item.subItems && handleToggle(item.text)}
              component={item.subItems ? "div" : Link}
              to={item.subItems ? undefined : item.path}
              sx={{
                justifyContent: open ? "initial" : "center",
                px: open ? 2 : 1
              }}
            >
              <ListItemIcon sx={{ color: "#00f5d4", minWidth: 0, mr: open ? 2 : "auto", justifyContent: "center" }}>
                {item.icon}
              </ListItemIcon>
              {open && 
                <ListItemText 
                  primary={item.text} 
                  sx={{
                    color: "#00f5d4",
                    "& .MuiTypography-root": {
                      color: "#00f5d4",
                    }
                  }}
                />
              }
            </ListItem>

            {item.subItems && (
              <Collapse in={openMenus[item.text]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ gap: 0.5 }}>
                  {item.subItems.map((sub) => (
                    <ListItem
                      button
                      key={sub.text}
                      component={Link}
                      to={sub.path}
                      sx={{
                        pl: open ? 4 : 2,
                        justifyContent: open ? "initial" : "center"
                      }}
                    >
                      <ListItemIcon sx={{ color: "#00f5d4", minWidth: 0, mr: open ? 2 : "auto", justifyContent: "center" }}>
                        {sub.icon}
                      </ListItemIcon>
                      {open && 
                        <ListItemText primary={sub.text} 
                          sx={{
                            color: "#00f5d4", 
                            "& .MuiTypography-root": {
                              color: "#00f5d4", 
                            }
                          }}
                        />
                      }
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Drawer>
  );
}

export default Sidebar;
